import { Injectable } from '@angular/core';

export type ConsoleLevel = 'log' | 'info' | 'warn' | 'error' | 'debug';

export interface ConsoleLogEntry {
  tsUtc: string;
  level: ConsoleLevel;
  message: string;
  repeatCount: number;
}

const MAX_ENTRIES = 500;
const MAX_MESSAGE_LENGTH = 2000;
const MAX_ENTRIES_PER_SECOND = 200;
export const PATCHED_CONSOLE_LEVELS: ConsoleLevel[] = ['log', 'info', 'warn', 'error', 'debug'];

/**
 * Patches console.log/info/warn/error/debug to keep a bounded ring buffer of recent output,
 * for the debug info export. Always passes through to the original console method.
 */
@Injectable({
  providedIn: 'root',
})
export class ConsoleCaptureService {
  private buffer: ConsoleLogEntry[] = [];
  private installed = false;
  private originalMethods = new Map<ConsoleLevel, Console[ConsoleLevel]>();

  private currentSecondBucket: number | null = null;
  private countInCurrentSecond = 0;
  private suppressedInCurrentSecond = 0;

  install(): void {
    if (this.installed) {
      return;
    }
    this.installed = true;

    /* eslint-disable no-console -- this is the console ring buffer patch itself */
    PATCHED_CONSOLE_LEVELS.forEach((level) => {
      const original = console[level].bind(console);
      this.originalMethods.set(level, console[level]);
      console[level] = (...args: unknown[]) => {
        original(...args);
        this.capture(level, args);
      };
    });
    /* eslint-enable no-console */
  }

  uninstall(): void {
    if (!this.installed) {
      return;
    }
    this.installed = false;

    /* eslint-disable no-console -- restoring the methods install() patched */
    this.originalMethods.forEach((original, level) => (console[level] = original));
    /* eslint-enable no-console */
    this.originalMethods.clear();
  }

  getEntries(): readonly ConsoleLogEntry[] {
    return [...this.buffer];
  }

  clear(): void {
    this.buffer = [];
    this.currentSecondBucket = null;
    this.countInCurrentSecond = 0;
    this.suppressedInCurrentSecond = 0;
  }

  private capture(level: ConsoleLevel, args: unknown[]): void {
    const now = Date.now();
    const bucket = Math.floor(now / 1000);

    if (this.currentSecondBucket === null) {
      this.currentSecondBucket = bucket;
    }

    if (bucket !== this.currentSecondBucket) {
      this.flushSuppressionMarker();
      this.currentSecondBucket = bucket;
      this.countInCurrentSecond = 0;
    }

    this.countInCurrentSecond++;

    if (this.countInCurrentSecond > MAX_ENTRIES_PER_SECOND) {
      this.suppressedInCurrentSecond++;
      return;
    }

    this.pushEntry({
      tsUtc: new Date(now).toISOString(),
      level,
      message: this.serializeArgs(args),
      repeatCount: 1,
    });
  }

  private flushSuppressionMarker(): void {
    if (this.suppressedInCurrentSecond === 0) {
      return;
    }

    this.pushEntry({
      tsUtc: new Date().toISOString(),
      level: 'warn',
      message: `${this.suppressedInCurrentSecond} messages suppressed`,
      repeatCount: 1,
    });
    this.suppressedInCurrentSecond = 0;
  }

  private pushEntry(entry: ConsoleLogEntry): void {
    const last = this.buffer[this.buffer.length - 1];

    if (last && last.level === entry.level && last.message === entry.message) {
      last.repeatCount++;
      last.tsUtc = entry.tsUtc;
      return;
    }

    this.buffer.push(entry);

    if (this.buffer.length > MAX_ENTRIES) {
      this.buffer.shift();
    }
  }

  private serializeArgs(args: unknown[]): string {
    const message = args.map((arg) => this.serializeArg(arg)).join(' ');
    return message.length > MAX_MESSAGE_LENGTH ? `${message.slice(0, MAX_MESSAGE_LENGTH)}…` : message;
  }

  private serializeArg(arg: unknown): string {
    if (typeof arg === 'string') {
      return arg;
    }

    try {
      return JSON.stringify(arg);
    } catch {
      return String(arg);
    }
  }
}
